import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/mui";

import { V1_STORY_KIND, formatDateTime, kindColor } from "./kinds";

type CreationDetail = {
  id: number | string;
  source: "creations" | "studio_stories" | "user_characters";
  kind: string;
  itemId: string | null;
  // A v1 story's creations.story_id; a Studio story's engine id (studio_stories.story_id).
  storyId: number | string;
  userId: number;
  userName?: string | null;
  title: string;
  status?: string;
  contentRating?: string | null;
  data?: unknown;
  settings?: unknown;
  // A character's engine record (user_characters.v2).
  v2?: unknown;
  createdAt: string;
  updatedAt: string;
};

/**
 * One row of the Creations list, read whole (`GET /api/v2/admin/creations/:id`).
 *
 * A Studio story (`story-<n>`) is its `studio_stories` index row: the facts the
 * engine keeps about the story file (rating, status, style, published entry…);
 * the story itself is a bucket file. A character (`character-<n>`) is its
 * `user_characters` row, with the engine's record in `v2`. A Story Studio item
 * (a world, voice, style, draft or a bin entry) is its `data` JSON, the engine's
 * own record. All of these are read-only (the server refuses an edit or a
 * delete). A v1 story shows its `settings` JSON and can be deleted.
 */
export const CreationShow = () => {
  const { query } = useShow<CreationDetail>({ resource: "admin/creations" });
  const { data, isLoading } = query;
  const record = data?.data;
  const v1 = record?.kind === V1_STORY_KIND;
  const item = record?.source === "creations" && !v1;
  // A story's or a character's row is shown whole: every column is a fact worth seeing.
  const json = !record ? null : v1 ? record.settings : item ? record.data : record;
  const jsonLabel = v1 ? "Settings (JSON)" : item ? "Data (JSON)" : "Record (JSON)";

  return (
    <Show
      isLoading={isLoading}
      resource="admin/creations"
      canEdit={false}
      canDelete={v1}
      title={<Typography variant="h5">{record?.title || record?.itemId || `Creation ${record?.id ?? ""}`}</Typography>}
    >
      {record ? (
        <Stack gap={2}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={record.kind} color={kindColor(record.kind)} />
            {record.status && !item ? <Chip size="small" variant="outlined" label={record.status} /> : null}
            {record.contentRating ? (
              <Chip
                size="small"
                variant="outlined"
                label={record.contentRating}
                color={record.contentRating === "mature" ? "warning" : "default"}
              />
            ) : null}
            <Typography variant="caption" color="text.secondary">
              id {record.id}
              {record.itemId ? ` · item ${record.itemId}` : ""}
              {record.storyId != null && !item ? ` · storyId ${record.storyId}` : ""} · userId {record.userId}
              {record.userName ? ` (${record.userName})` : ""} · created {formatDateTime(record.createdAt)} · updated{" "}
              {formatDateTime(record.updatedAt)}
            </Typography>
          </Stack>

          <Box>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              {jsonLabel}
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 1,
                bgcolor: "action.hover",
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.5,
                overflow: "auto",
                maxHeight: "70vh",
                whiteSpace: "pre",
              }}
            >
              {json == null ? "null" : JSON.stringify(json, null, 2)}
            </Box>
          </Box>
        </Stack>
      ) : null}
    </Show>
  );
};
